// This device's finished games, and the posting of each one (CLAUDE.md: the
// data layer, step 4).
//
// **Two records of one game, for two different jobs.** The server keeps a
// result per device per puzzle for the statistics everybody sees
// (record_result); this keeps the same outcome here, for what only this player
// sees — the Stats window and the Archive's tiles. "Your stats are stored on
// this device" is literally true: nothing reads them back from the server.
//
// **Every finished game is recorded, and none twice.** It is written here first,
// marked pending, and then posted; a post that fails stays pending and is tried
// again on the next visit (`flushPending`), so a dropped connection costs a
// delay, never a result. The server's answer settles whether the game counts —
// a Running Order played on its own day does, an archive round does not —
// because the server knows what day it is in the UK and the device may not.
//
// A number and an outcome per puzzle, so it stays small however long somebody
// plays, and it is never pruned: a streak is worked out from the whole of it.
import { rpc } from './api.ts';
import { deviceId, forgetDevice } from './device.ts';
import { statsFrom, type DeviceStats } from './stats.ts';

export type { GameKey } from './stats.ts';
import type { GameKey } from './stats.ts';
const SERVER_GAME: Record<GameKey, string> = { 'running-order': 'running_order', 'call-sheet': 'call_sheet' };

export interface Outcome {
  /** The guess it was solved on; null is a miss. */
  solvedOn: number | null;
  /** Counts toward stats and the streak: a daily on its day, a Call Sheet's
   *  first completion. */
  counts: boolean;
  /** When it was finished. */
  at: number;
  /** Not yet accepted by the server; the guesses are kept until it is. */
  pending?: unknown[];
}

const KEY = 'cinemore-history';

/**
 * **Include my results in site statistics** — on unless the player turns it off
 * on the Privacy page. Off, a finished game is kept on this device and nothing
 * about it is sent: the Stats window and the archive work from this device's
 * record exactly as before. Turning it off does not reach back to results
 * already sent; Delete my results does that.
 */
export const SHARE_KEY = 'cinemore-share-results';

export function sharingResults(): boolean {
  try { return localStorage.getItem(SHARE_KEY) !== 'off'; } catch { return true; }
}

export function setSharingResults(on: boolean): void {
  try {
    if (on) localStorage.removeItem(SHARE_KEY);
    else localStorage.setItem(SHARE_KEY, 'off');
  } catch { /* the choice cannot be kept; results are then sent as before */ }
}
type Store = Partial<Record<GameKey, Record<string, Outcome>>>;

function read(): Store {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(KEY) ?? 'null');
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as Store : {};
  } catch {
    return {};
  }
}

function write(store: Store): void {
  try { localStorage.setItem(KEY, JSON.stringify(store)); } catch { /* blocked or full */ }
}

function put(game: GameKey, number: number, outcome: Outcome): void {
  const store = read();
  store[game] = { ...(store[game] ?? {}), [String(number)]: outcome };
  write(store);
}

/** Every finished game of one kind on this device, by puzzle number. */
export function outcomes(game: GameKey): Map<number, Outcome> {
  const out = new Map<number, Outcome>();
  for (const [n, o] of Object.entries(read()[game] ?? {})) {
    const number = Number(n);
    if (Number.isInteger(number) && number > 0 && o && typeof o === 'object') out.set(number, o);
  }
  return out;
}

interface Recorded { recorded: boolean; solvedOn: number | null; counts: boolean }

/** Posts in flight, so a result screen can wait for its own result to be
 *  counted before it asks how everybody did. */
const inflight = new Map<string, Promise<void>>();

/** Settles once this device's result for the puzzle has been dealt with —
 *  posted, refused, kept off the server by the statistics switch, or never
 *  needing a post because it was already recorded. Never rejects. */
export function recorded(game: GameKey, number: number): Promise<void> {
  return inflight.get(`${game}:${number}`) ?? Promise.resolve();
}

async function post(game: GameKey, number: number, guesses: unknown[]): Promise<void> {
  const answer = await rpc<Recorded>('record_result', {
    p_game: SERVER_GAME[game], p_number: number, p_guesses: guesses, p_device: deviceId()
  });
  const current = outcomes(game).get(number);
  // The server's word on the outcome and on whether it counts. A result it
  // already had from this device ("recorded: false") is settled just the same.
  put(game, number, { solvedOn: answer.solvedOn, counts: answer.counts, at: current?.at ?? Date.now() });
}

/**
 * A game has just finished: keep it here, and post it.
 *
 * `counts` is this device's best guess, used only until the server answers:
 * true for a Call Sheet, and for a Running Order whether it was today's —
 * which may still be being looked up, so it can be a promise. **The work is
 * registered at once, before that lookup**, so a result screen asking
 * `recorded()` straight away waits for this result rather than racing it.
 * Nothing happens for a game already recorded or already on its way.
 */
export function recordFinished(game: GameKey, number: number, guesses: unknown[],
                               solvedOn: number | null, counts: boolean | Promise<boolean>): void {
  const known = outcomes(game).get(number);
  if (known && !known.pending) return;
  const key = `${game}:${number}`;
  if (inflight.has(key)) return;
  const work = Promise.resolve(counts).catch(() => false).then((c) => {
    // Kept out of the statistics: this device's record only, with its own guess
    // at whether the game counts, and nothing pending, so nothing is ever sent.
    if (!sharingResults()) {
      put(game, number, { solvedOn, counts: c, at: known?.at ?? Date.now() });
      return;
    }
    put(game, number, { solvedOn, counts: c, at: known?.at ?? Date.now(), pending: guesses });
    return post(game, number, guesses);
  })
    .catch(() => { /* stays pending, tried again next visit */ })
    .finally(() => inflight.delete(key));
  inflight.set(key, work);
}

/** Post whatever an earlier visit could not. Called once as the site loads. */
export function flushPending(): void {
  if (!sharingResults()) return;
  for (const game of Object.keys(SERVER_GAME) as GameKey[]) {
    for (const [number, o] of outcomes(game)) {
      if (Array.isArray(o.pending)) post(game, number, o.pending).catch(() => {});
    }
  }
}

/**
 * **Delete my results**: everything recorded against this device's id on the
 * server, then everything Cinemore keeps on this device. The statistics choice
 * survives — somebody who kept their results out and then deletes them wants
 * them kept out from now on too. Returns how many results the server removed.
 */
export async function deleteMyResults(): Promise<number> {
  const removed = await rpc<number>('delete_my_results', { p_device: deviceId() });
  forgetDevice([SHARE_KEY]);
  return removed;
}

// ---- the figures the Stats window shows (the arithmetic is in stats.ts) ----

/** This device's figures for one game: see `statsFrom`. */
export function statsFor(game: GameKey, today: number | null): DeviceStats | null {
  return statsFrom(game, outcomes(game), today);
}
